<template>
  <a-drawer
    :width="900"
    :visible="visible"
    :unmount-on-close="true"
    :mask-closable="false"
    hide-cancel
    @ok="handleOk"
    @cancel="handleCancel"
  >
    <template #title>
      <a-space>
        {{ title || '回访记录' }}
        <a-tooltip :content="$t('followTable.add_follow_up_plan')">
          <a-button type="primary" size="mini" @click="showFollowModal">
            <template #icon>
              <icon-plus />
            </template>
            {{ $t('followTable.add_follow_up_plan') }}
          </a-button>
        </a-tooltip>
      </a-space>
    </template>

    <a-table
      :row-key="(record: FollowUpPlanRecord) => record.id"
      :columns="followUpColumns"
      :data="planData"
      :pagination="planPagination"
      :loading="loading"
    >
      <template #index="{ rowIndex }">
        {{
          rowIndex + 1 + (planPagination.current - 1) * planPagination.pageSize
        }}
      </template>
      <template #remind_time="{ record }">
        {{ record.remind_time.slice(0, 10) }}
      </template>
      <template #content="{ record }">
        {{
          record.type == 'system' && record.content == ''
            ? '默认回访计划'
            : record.content
        }}
      </template>
      <template #staff_name="{ record }">
        {{ record.staff_name || '-' }}
      </template>
      <template #operations="{ record }">
        <a-space>
          <a-button
            type="primary"
            size="mini"
            @click="showRecordModal(record.log, record.id)"
          >
            回访记录
          </a-button>
          <a-button type="text" size="mini" @click="handleDelete(record)">
            <template #icon>
              <IconDelete />
            </template>
          </a-button>
        </a-space>
      </template>
    </a-table>

    <!-- 添加回访计划Modal -->
    <FollowUpPlanModal
      v-model:visible="addFollowUpVisible"
      :user-id="userId"
      @success="handleFollowUpPlanSuccess"
    />
  </a-drawer>

  <a-modal v-model:visible="recordModalVisible" title="回访记录">
    <SecondLevelTable
      :params-data="recordData"
      :user-id="props.userId"
      :reminder-id="currentReminderId"
      @update:data="handleUpdateData"
    />
  </a-modal>
</template>

<script lang="ts" setup>
  import { ref, reactive, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { IconPlus, IconDelete } from '@arco-design/web-vue/es/icon';
  import {
    deleteFollowUpPlan,
    queryFollowUpPlanList,
    queryFollowUpRecordList,
    FollowUpPlanListParams,
    FollowUpPlanRecord,
  } from '@/api/followup';
  import FollowUpPlanModal from '@/components/follow-up-plan-modal/index.vue';
  import useLoading from '@/hooks/loading';
  import { Message, Modal } from '@arco-design/web-vue';
  import { useUserStore } from '@/store';
  import SecondLevelTable from '@/components/secondLevelTable/secondLevelTable.vue';

  interface Props {
    visible: boolean;
    userId: number;
    title?: string;
  }

  interface Emits {
    (e: 'update:visible', value: boolean): void;
    (e: 'success'): void;
  }

  const props = withDefaults(defineProps<Props>(), {
    title: '',
  });

  const emit = defineEmits<Emits>();

  const { t } = useI18n();
  const { loading, setLoading } = useLoading(false);
  const userStore = useUserStore();

  const addFollowUpVisible = ref(false);
  const recordModalVisible = ref(false);
  const currentReminderId = ref<number>(0);
  const recordData = ref<
    {
      user_id: number;
      reminder_id: number;
      result_level: number | string;
      content: string;
    }[]
  >([]);

  const showRecordModal = (
    log: FollowUpPlanRecord['log'],
    reminderId?: number
  ) => {
    recordData.value = log || [];
    if (reminderId) {
      currentReminderId.value = reminderId;
    }
    recordModalVisible.value = true;
  };

  const handleUpdateData = async (data: any) => {
    console.log('更新数据', data);

    // 如果是刷新请求，重新获取回访记录数据
    if (data === 'refresh') {
      await refreshRecordData();
    }

    // 重新获取回访计划数据
    await fetchPlan();
  };

  // 刷新回访记录数据
  const refreshRecordData = async () => {
    try {
      // 根据当前的reminderId重新获取回访记录
      if (currentReminderId.value) {
        const { data } = await queryFollowUpRecordList({
          page: 1,
          pageSize: 100,
          reminder_id: currentReminderId.value,
        } as any);

        // 转换数据格式以匹配recordData的类型
        recordData.value = data.list.map((item) => ({
          user_id: item.user_id,
          reminder_id: currentReminderId.value,
          result_level: item.result_level,
          content: item.content,
          createtime: item.createtime,
        }));
      }
    } catch (error) {
      console.error('刷新回访记录失败:', error);
      // 如果API调用失败，清空数据
      recordData.value = [];
    }
  };

  const followUpColumns = computed(() => {
    const baseColumns: any[] = [
      {
        title: t('followTable.columns.index'),
        dataIndex: 'index',
        slotName: 'index',
        width: 80,
      },
      {
        title: '回访时间',
        dataIndex: 'remind_time',
        slotName: 'remind_time',
        width: 120,
      },
      {
        title: '计划内容',
        dataIndex: 'content',
        slotName: 'content',
        width: 300,
      },
    ];

    // 只有部门为3的用户才显示回访人列
    if (userStore.userInfo?.department === 3) {
      baseColumns.push({
        title: '回访人',
        dataIndex: 'staff_name',
        slotName: 'staff_name',
        width: 120,
      });
    }

    // 添加操作列
    baseColumns.push({
      title: '操作',
      dataIndex: 'operations',
      slotName: 'operations',
      width: 100,
      fixed: 'right',
    });

    return baseColumns;
  });

  const planData = ref<FollowUpPlanRecord[]>([]);
  const planPagination = reactive({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const fetchPlan = async (
    params: FollowUpPlanListParams = {
      page: 1,
      pageSize: 20,
      user_id: props.userId,
    }
  ) => {
    setLoading(true);
    try {
      const { data } = await queryFollowUpPlanList({
        ...params,
      });
      planData.value = data.list;
      planPagination.current = params.page;
      planPagination.total = data.total;
    } catch (err) {
      // console.error('获取回访计划失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOk = () => {
    emit('update:visible', false);
  };

  const handleCancel = () => {
    emit('update:visible', false);
  };

  const showFollowModal = () => {
    addFollowUpVisible.value = true;
  };

  const handleFollowUpPlanSuccess = () => {
    // console.log('添加回访计划成功');
    fetchPlan();
    emit('success');
  };

  const handleDelete = async (record: FollowUpPlanRecord) => {
    try {
      // 显示确认对话框
      await new Promise((resolve, reject) => {
        Modal.confirm({
          title: '确认删除',
          content: '您确定要删除这条回访计划吗？删除后无法恢复。',
          okText: '确定',
          cancelText: '取消',
          onOk: resolve,
          onCancel: reject,
        });
      });

      // 用户确认后执行删除操作
      await deleteFollowUpPlan(record.id);
      fetchPlan();
      Message.success('删除成功');
      emit('success');
    } catch (error) {
      // 如果用户取消删除，不显示错误信息
      if (error !== undefined) {
        // console.error('删除回访计划失败:', error);
        Message.error('删除失败');
      }
    }
  };

  // 监听visible和userId变化，当打开时获取数据
  watch(
    () => [props.visible, props.userId],
    ([newVisible, newUserId], oldValues) => {
      // 只有当drawer打开且有有效的userId时才获取数据
      if (newVisible && newUserId) {
        const [oldVisible, oldUserId] = oldValues || [false, 0];
        // 避免重复请求：只有当visible从false变为true，或者userId发生变化时才请求
        if (
          (!oldVisible && newVisible) ||
          (newVisible && newUserId !== oldUserId)
        ) {
          fetchPlan();
        }
      }
    },
    { immediate: true }
  );
</script>

<script lang="ts">
  export default {
    name: 'FollowUpRecordDrawer',
  };
</script>
